import moment from "moment";

import { Dispatch, GetState } from "common/types/redux";

import { getClient } from "services";
import errorHandler from "services/courseService/errorHandler";

import {
  isSignedIn,
  isSignedInAsGuest
} from "seneca-common/features/user/state";
import { awaitCondition } from "seneca-common/utils/state/await-condition";

// TODO: don't import from features
import { getCourseId } from "features/classroom/state";

import { courseEnrollmentsNeedFetching } from "../../selectors";
import {
  CourseEnrollment,
  createCourseEnrollmentActionFactory,
  deleteCourseEnrollmentActionFactory,
  deleteCourseEnrollment as deleteCourseEnrollmentLocally,
  fetchAllCourseEnrollmentsActionFactory,
  getFetchCourseEnrollmentsFinished,
  isEnrolledInCourse,
  receiveCourseEnrollments,
  saveNewCourseEnrollment as saveNewCourseEnrollmentInStore
} from "../../state";

const getCourseServiceClient = () => getClient("courseService");

export const fetchEnrolledCourses = fetchAllCourseEnrollmentsActionFactory({
  requestFunction: options =>
    getCourseServiceClient().fetchUsersCourseEnrollments(options),
  storeAction: receiveCourseEnrollments
});

const saveNewCourseEnrollmentAction = createCourseEnrollmentActionFactory({
  requestFunction: (courseId: string) =>
    getCourseServiceClient().enrollUserInCourse(courseId),
  errorHandler
});

const deleteCourseEnrollmentAction = deleteCourseEnrollmentActionFactory({
  requestFunction: (courseId: string) =>
    getCourseServiceClient().unenrollUserFromCourse(courseId)
});

export const fetchEnrolledCoursesIfNotFetched =
  () => (dispatch: Dispatch, getState: GetState) => {
    const state = getState();

    if (courseEnrollmentsNeedFetching(state)) {
      return dispatch(fetchEnrolledCourses());
    } else {
      return dispatch(awaitCondition(getFetchCourseEnrollmentsFinished));
    }
  };

export const saveNewCourseEnrollment = (courseId: string) => {
  return async (dispatch: Dispatch) => {
    const output = await dispatch(
      saveNewCourseEnrollmentAction(courseId, courseId)
    );

    if (!(output instanceof Error)) {
      dispatch(saveNewCourseEnrollmentLocally(courseId));
    }
  };
};

export const saveNewCourseEnrollmentLocally = (courseId: string) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const enrollment = new CourseEnrollment({
      courseId,
      timeEnrolled: moment().format("YYYY-MM-DD HH:mm:ss")
    });
    dispatch(saveNewCourseEnrollmentInStore(enrollment));
  };
};

export const saveNewCourseEnrollmentLocallyIfNotAlreadyEnrolled = (
  courseId: string
) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const signedIn = isSignedIn(state);

    if (signedIn) {
      const alreadyEnrolled = isEnrolledInCourse(state, courseId);

      if (!alreadyEnrolled) {
        return dispatch(saveNewCourseEnrollmentLocally(courseId));
      }
    }
  };
};

export const saveNewCourseEnrollmentIfNotAlreadyEnrolled = (
  courseId: string
) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    const state = getState();
    const signedIn = isSignedIn(state);
    const signedInAsGuest = isSignedInAsGuest(state);

    if (signedIn) {
      await dispatch(fetchEnrolledCoursesIfNotFetched());

      const newState = getState();
      const alreadyEnrolled = isEnrolledInCourse(newState, courseId);

      if (!alreadyEnrolled) {
        return dispatch(saveNewCourseEnrollment(courseId));
      }
    }

    if (signedInAsGuest) {
      return dispatch(saveNewCourseEnrollment(courseId));
    }
  };
};

export const deleteCourseEnrollment = (courseId: string) => {
  return async (dispatch: Dispatch, getState: GetState) => {
    dispatch(deleteCourseEnrollmentAction(courseId));
    dispatch(deleteCourseEnrollmentLocally(courseId));
  };
};

export const reconcileCourseEnrolments = () => {
  return async function (dispatch: Dispatch, getState: GetState) {
    const state = getState();

    const courseId = await getCourseId(state);

    if (courseId) {
      await dispatch(saveNewCourseEnrollmentIfNotAlreadyEnrolled(courseId));
    }
  };
};
